import {Divider, Grid, Typography, makeStyles} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitArrayPaths} from '../Common/PropertyPaths';

const useSectionLabelStyles = makeStyles(() => ({
  divider: {
    width: '100%',
    margin: '0.25rem 0',
  },
}));

const coverageIndex = 0;
const benefitName = 'cancerConditionsDetails.conditionsDetails';

const InitialDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const initialPath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 0);

  const initialRows: GridRowProps[] = [
    {
      rowProps: {key: 'initial-cancer-conditions-row-1'},
      items: [
        {
          key: 'initial-cancer-conditions-invasive-cancer',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Invasive Cancer (100%)"
              value={getQuoteFieldValue<number>(initialPath.invasiveCancerPct)}
              id={initialPath.invasiveCancerPct}
              onChange={e => updateQuoteFieldValue(initialPath.invasiveCancerPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'initial-cancer-conditions-row-2'},
      items: [
        {
          key: 'initial-cancer-conditions-carcinoma',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Carcinoma in Situ (25%)"
              value={getQuoteFieldValue<number>(initialPath.carcinomaSituPct)}
              id={initialPath.carcinomaSituPct}
              onChange={e => updateQuoteFieldValue(initialPath.carcinomaSituPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={initialRows} />;
};

const RecurrenceDiagnosisBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const recurrencePath = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName, 1);

  const recurranceRows: GridRowProps[] = [
    {
      rowProps: {key: 'recurrance-cancer-conditions-row-1'},
      items: [
        {
          key: 'recurrance-cancer-conditions-invasive-cancer',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Invasive Cancer"
              value={getQuoteFieldValue<number>(recurrencePath.invasiveCancerPct)}
              id={recurrencePath.invasiveCancerPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.invasiveCancerPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'recurrance-cancer-conditions-row-2'},
      items: [
        {
          key: 'recurrance-cancer-conditions-carcinoma',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Carcinoma in Situ"
              value={getQuoteFieldValue<number>(recurrencePath.carcinomaSituPct)}
              id={recurrencePath.carcinomaSituPct}
              onChange={e => updateQuoteFieldValue(recurrencePath.carcinomaSituPct, Number(e.target.value) / 100)}
              adornment="percentage"
              disabled
            />
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];
  return <FormRowGrid rows={recurranceRows} />;
};

export const CancerConditionsBlock = () => {
  const classes = useSectionLabelStyles();
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'cancer-condition-divider'},
      items: [
        {
          key: 'cancer-condition-divider',
          element: <Divider className={classes.divider} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'cancer-condition-row-1'},
      items: [
        {
          key: 'cancer-condition-column-1',
          element: (
            <Grid spacing={1} style={{padding: '0 2rem'}} container alignItems="center">
              <Typography component="span" variant="subtitle1" style={{fontSize: '0.875rem'}}>
                Initial Diagnosis (% of Plan Benefit)
              </Typography>
              <InitialDiagnosisBlock />
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: 'cancer-condition-column-2',
          element: (
            <Grid spacing={1} style={{padding: '0 2rem'}} container alignItems="center">
              <Typography component="span" variant="subtitle1" style={{fontSize: '0.875rem'}}>
                Recurrence (% of Plan Benefit)
              </Typography>
              <RecurrenceDiagnosisBlock />
            </Grid>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Cancer Conditions" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
